import React, { useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { useUnavailableWorkplacesForDates } from '../../hooks/floorplan.hooks';
import useWorkplaceOccupants from '../../hooks/workplace-occupants.hooks';
import FloorPlanViewer from '../../components/floor-plan-viewer.component';
import type FloorPlan from '../../../dashboard/models/floor-plan';
import Column from '../../../../core/components/column/column.component';
import Row from '../../../../core/components/row/row.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../../core/components/center/center.component';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import StyleConstants from '../../../../core/styles.constants';
import { type DayPart } from '../../models/daypart-entry';
import * as sharedStyles from '../../modals/workplace-booking-modal.styles';
import * as sectionStyles from '../../check-in-employees.styles';

interface Props {
  readonly dayPart: DayPart;
  readonly officeId: string | undefined;
  readonly selectedDates: Date[];
  readonly floorPlans: FloorPlan[] | undefined;
  readonly roomWorkplaceIds: string[];
  /** Desks out of service on one or more of the selected days; not drawn at all. */
  readonly retiredWorkplaceIds?: readonly string[];
  readonly selectedWorkplaceId: string | undefined;
  readonly onWorkplaceSelected: (workplaceId: string) => void;
}

const DayPartFloorPlanSection = ({
  dayPart,
  officeId,
  selectedDates,
  floorPlans,
  roomWorkplaceIds,
  retiredWorkplaceIds = [],
  selectedWorkplaceId,
  onWorkplaceSelected,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const storeys = useMemo(() => {
    if (!floorPlans) return [];
    return Array.from(new Set(floorPlans.map((fp) => fp.storey))).sort((a, b) => a - b);
  }, [floorPlans]);

  const [selectedStorey, setSelectedStorey] = useState<number>(storeys[0] ?? 0);

  useEffect(() => {
    if (storeys.length > 0 && !storeys.includes(selectedStorey)) {
      setSelectedStorey(storeys[0]);
    }
  }, [storeys, selectedStorey]);

  // Auto-select the storey that contains the room's workplaces (for pre-selected rooms on edit)
  useEffect(() => {
    if (!floorPlans || roomWorkplaceIds.length === 0) return;
    const matchingPlan = floorPlans.find((fp) => (
      fp.workplacesLayer?.features?.some(
        (f) => f.properties?.featureId != null && roomWorkplaceIds.includes(f.properties.featureId),
      )
    ));
    if (matchingPlan != null) {
      setSelectedStorey(matchingPlan.storey);
    }
  }, [floorPlans, roomWorkplaceIds]);

  const currentFloorPlan = useMemo(() => {
    if (!floorPlans) return undefined;
    return floorPlans.find((fp) => fp.storey === selectedStorey);
  }, [floorPlans, selectedStorey]);

  const unavailableState = useUnavailableWorkplacesForDates({
    officeId,
    dates: selectedDates.map((d) => d.toDateString()),
    dayPart,
    // This screen is an administrator seating somebody who is standing in front of them, so it
    // asks what the office actually looks like. Without this the server drops today's morning
    // bookings once the morning is over, every desk draws free, and two people get the same one.
    includeElapsedDayPart: true,
  });

  const unavailableWorkplaceIds = unavailableState.unavailableWorkplaceIds ?? [];

  // The same days and half-day the availability call above asks about, including the elapsed
  // one: the two have to agree, or a desk drawn as booked would hover as empty after noon.
  const occupantsState = useWorkplaceOccupants({
    officeId,
    dates: selectedDates.map((date) => date.toDateString()),
    dayPart,
    includeElapsedDayPart: true,
  });

  const handleWorkplaceClick = (featureId: string): void => {
    if (unavailableWorkplaceIds.includes(featureId)) return;
    if (roomWorkplaceIds.length > 0 && !roomWorkplaceIds.includes(featureId)) return;
    onWorkplaceSelected(featureId === selectedWorkplaceId ? '' : featureId);
  };

  if (!floorPlans || floorPlans.length === 0) {
    return (
      <span style={{ fontSize: StyleConstants.TERTIARY_FONT_SIZE, color: StyleConstants.LIGHT_GREY_COLOR, fontStyle: 'italic' }}>
        {t('workplace-booking-no-floorplan')}
      </span>
    );
  }

  return (
    <div className={style(sectionStyles.inModalSectionCard(isMobile))}>
      <Column itemSpacing={12}>
        <span className={style(sharedStyles.sectionHeader)}>{t('workplace-booking-floorplan-section-title')}</span>

        {storeys.length > 1 && (
          <Column itemSpacing={4}>
            <span className={style(sharedStyles.labelText)}>{t('workplace-booking-select-storey')}</span>
            <Row itemSpacing={8}>
              {storeys.map((storey) => (
                <button
                  key={storey}
                  type="button"
                  className={style(sharedStyles.storeyButton(storey === selectedStorey))}
                  onClick={(): void => {
                    setSelectedStorey(storey);
                  }}
                >
                  {t('floor-plan-storey')} {storey}
                </button>
              ))}
            </Row>
          </Column>
        )}

        {unavailableState.initialized || unavailableState.error ? (
          <Column itemSpacing={8}>
            <span className={style(sharedStyles.infoText)}>{t('workplace-booking-click-to-select')}</span>
            {currentFloorPlan ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FloorPlanViewer
                  floorPlan={currentFloorPlan}
                  unavailableWorkplaceIds={unavailableWorkplaceIds}
                  hiddenWorkplaceIds={retiredWorkplaceIds}
                  occupantsByWorkplaceId={occupantsState.occupantsByWorkplaceId}
                  occupantsIncludeContactDetails={occupantsState.containsContactDetails}
                  roomWorkplaceIds={roomWorkplaceIds}
                  selectedWorkplaceId={selectedWorkplaceId}
                  onWorkplaceClick={handleWorkplaceClick}
                  height={isMobile ? 360 : 520}
                />
              </div>
            ) : (
              <span style={{ fontSize: StyleConstants.TERTIARY_FONT_SIZE, color: StyleConstants.LIGHT_GREY_COLOR, fontStyle: 'italic' }}>
                {t('workplace-booking-no-floorplan-for-storey')}
              </span>
            )}
            {selectedWorkplaceId && (
              <span className={style(sharedStyles.selectedWorkplaceText)}>
                {t('workplace-booking-selected')}
              </span>
            )}
          </Column>
        ) : (
          <Center>
            <ActivityIndicator size={16} color={StyleConstants.GREY_COLOR} />
          </Center>
        )}
      </Column>
    </div>
  );
};

export default DayPartFloorPlanSection;
